import schemeDefs from "../schemes";
import type { CodeScheme, SchemeDefinition } from "../types";

/** localStorage key used to persist the user's selected color scheme. */
export const SCHEME_STORAGE_KEY = "techdocs.addons.settings.code-scheme";

/** Custom event name dispatched when the active color scheme changes. */
export const SCHEME_CHANGE_EVENT = "code-scheme-change";

/** Name of the color scheme applied on first load when no stored preference exists. */
export const DEFAULT_SCHEME_NAME = "One Dark";

/** Maps each {@link SchemeColors} key to one or more MkDocs CSS custom property names. */
const COLOR_KEY_TO_VAR: Record<string, string[]> = {
    foreground: ["--md-code-fg-color"],
    background: ["--md-code-bg-color"],
    keyword: ["--md-code-hl-keyword-color"],
    function: ["--md-code-hl-function-color"],
    string: ["--md-code-hl-string-color"],
    number: ["--md-code-hl-number-color"],
    constant: ["--md-code-hl-constant-color", "--md-code-hl-special-color"],
    name: ["--md-code-hl-name-color"],
    comment: ["--md-code-hl-comment-color", "--md-code-hl-generic-color"],
    variable: ["--md-code-hl-variable-color"],
    operator: ["--md-code-hl-operator-color"],
    punctuation: ["--md-code-hl-punctuation-color"],
};

/**
 * Builds supplemental Pygments class overrides not covered by MkDocs CSS variables.
 *
 * @param colors - Raw color map from a scheme definition.
 * @returns CSS rules string targeting `.highlight` Pygments token classes.
 */
function extraRules(colors: Record<string, string>): string {
    const fn = colors.function ?? colors.foreground;
    const nm = colors.name ?? colors.foreground;
    const constant = colors.constant ?? colors.foreground;
    return [
        `.highlight .nx { color: ${fn}; }`,
        `.highlight .na { color: ${nm}; }`,
        `.highlight .nd { color: ${fn}; }`,
        `.highlight .bp { color: ${constant}; }`,
    ].join("\n");
}

/**
 * Converts a color map into a CSS string scoped to the TechDocs shadow host.
 *
 * @param colors - Raw color map from a scheme definition.
 * @returns CSS string setting MkDocs custom properties and extra Pygments overrides.
 */
function buildCss(colors: Record<string, string>): string {
    const varDecls = Object.entries(colors)
        .filter(([key]) => key in COLOR_KEY_TO_VAR)
        .flatMap(([key, hex]) =>
            COLOR_KEY_TO_VAR[key].map(cssVar => `${cssVar}: ${hex};`)
        );

    return `:host > * { ${varDecls.join(" ")} }\n${extraRules(colors)}`;
}

/**
 * Converts a raw {@link SchemeDefinition} into a {@link CodeScheme} with compiled CSS strings.
 *
 * @param def - Scheme definition loaded from a JSON file in `schemes/`.
 * @returns Compiled scheme ready for injection into the TechDocs shadow DOM.
 */
function parseScheme(def: SchemeDefinition): CodeScheme {
    return {
        name: def.name,
        dark: buildCss(def.dark),
        light: buildCss(def.light),
    };
}

/**
 * Sentinel scheme that represents the Backstage platform default.
 * Provides empty CSS strings so MkDocs falls back to its own stylesheet.
 */
const BACKSTAGE_DEFAULT: CodeScheme = {
    name: "Backstage Default",
    dark: "",
    light: "",
};

/**
 * Complete list of available color schemes, compiled from JSON definitions plus the Backstage default.
 *
 * @remarks
 * JSON files are loaded dynamically via `schemes/index.ts` (Webpack `require.context`).
 * The Backstage Default entry is always appended last.
 */
export const ALL_SCHEMES: CodeScheme[] = [
    ...schemeDefs.map(def => parseScheme(def)),
    BACKSTAGE_DEFAULT,
];
